import { useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { api, errMsg, uid } from "../../lib/api";

import { useSpamProtection } from "./SpamProtection";

import VenueModels from "./VenueModels";

const PHOTO_TYPES = ["interior", "artist_application", "venue_application"];

export function InquiryForm({ type, object, variant, onDone, defaultMessage = "" }) {
  const spam = useSpamProtection();
  const token = useRef(uid());
  const started = useRef(Date.now());
  const [f, setF] = useState({ name: "", email: "", phone: "", message: defaultMessage, budget: "", location: "", timeframe: "", privacy: false, venue_model: "undecided", venue_kind: "local", rotation_interest: false });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [res, setRes] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (busy || !spam.ready) return;
    setErr("");
    if (type === "artist_application" && !f.email.trim()) return setErr("Bitte deine eigene E-Mail für den Künstlerzugang angeben.");
    if (!f.email && !f.phone) return setErr("Bitte E-Mail oder Telefon/WhatsApp angeben.");
    if (!f.privacy) return setErr("Bitte den Datenschutzhinweis bestätigen.");
    if (files.some((x) => x.size > 8 * 1024 * 1024)) return setErr("Jedes Foto darf höchstens 8 MB groß sein.");
    setBusy(true);
    const fd = new FormData();
    fd.append("type", type);
    if (type === "venue_application") { fd.append("venue_model", f.venue_model); fd.append("venue_kind", f.venue_kind); fd.append("rotation_interest", String(f.rotation_interest)); }
    fd.append("turnstile_token", spam.token);
    ["name", "email", "phone", "message", "budget", "location", "timeframe"].forEach((k) => fd.append(k, f[k]));
    fd.append("object_type", object?.type || "");
    fd.append("object_id", object?.id || "");
    fd.append("object_title", object?.title || "");
    fd.append("variant", variant || "");
    fd.append("qr_code", sessionStorage.getItem("kf_qr") || "");
    fd.append("source", sessionStorage.getItem("kf_src") || "");
    fd.append("client_token", token.current);
    fd.append("elapsed_ms", String(Date.now() - started.current));
    fd.append("privacy_ack", "true");
    files.forEach((x) => fd.append("photos", x));
    try {
      const r = await api.post("/inquiries", fd);
      setRes(r.data);
      onDone && onDone(r.data);
    } catch (e2) {
      setErr(errMsg(e2));
    } finally {
      spam.reset();
      setBusy(false);
    }
  };

  if (res) {
    return (
      <div className="space-y-3" data-testid="inquiry-success">
        <p className="font-display font-black text-2xl">Danke, deine Anfrage ist gespeichert.</p>
        <p className="text-sm">Referenz: <span className="font-mono">{res.id.slice(0, 8)}</span></p>
        {res.mail_status === "sent" ? <p className="text-sm">Wir wurden per E-Mail benachrichtigt{res.receipt_status === "sent" ? " und du erhältst eine Eingangsbestätigung." : "."}</p>
          : <p className="text-sm bg-[#FFF9E6] border border-[#FFC72C] p-3" data-testid="inquiry-mail-pending">Hinweis: Der E-Mail-Versand ist derzeit nicht eingerichtet bzw. fehlgeschlagen. Deine Anfrage liegt sicher bei uns, eine Bestätigungsmail wurde aber noch nicht verschickt. Wir melden uns persönlich.</p>}
      </div>
    );
  }
  const photos = PHOTO_TYPES.includes(type);
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="inquiry-form" noValidate>
      {type === "venue_application" && <><VenueModels /><label className="block text-sm">Was für ein Ort?<select className="border p-3 w-full" value={f.venue_kind} onChange={set("venue_kind")}><option value="local">Lokal / Bar / Café</option><option value="practice">Ordination / Praxis</option><option value="studio">Studio / Atelier</option><option value="law_firm">Anwaltskanzlei</option><option value="real_estate">Immobilienfirma / Musterwohnung</option><option value="hotel">Hotel</option><option value="office">Büro / Unternehmen</option><option value="other">Anderer Ort</option></select></label><label className="block text-sm">Was interessiert euch?<select className="border p-3 w-full" value={f.venue_model} onChange={set("venue_model")}><option value="undecided">Gemeinsam besprechen</option><option value="exhibition_partner">Aktiver Ausstellungspartner</option><option value="rental">Kunst zur Raumgestaltung / Miete</option></select></label><label className="flex gap-3 items-start text-sm"><input type="checkbox" className="mt-1 h-5 w-5" checked={f.rotation_interest} onChange={set("rotation_interest")} /><span>Wir interessieren uns auch für einen regelmäßigen Bilderwechsel. Bitte macht uns ein Angebot.</span></label></>}
      {object?.title && <p className="text-sm bg-[#F4EFEA] px-3 py-2" data-testid="inquiry-object">Betrifft: <strong>{object.title}</strong>{variant ? ` · ${variant}` : ""}</p>}
      <div className="grid sm:grid-cols-2 gap-4">
        <div><Label htmlFor="iq-name">Name *</Label><Input id="iq-name" required minLength={2} value={f.name} onChange={set("name")} autoComplete="name" data-testid="inquiry-name" /></div>
        <div><Label htmlFor="iq-email">E-Mail{type === "artist_application" ? " *" : ""}</Label><Input id="iq-email" type="email" required={type === "artist_application"} value={f.email} onChange={set("email")} autoComplete="email" data-testid="inquiry-email" /></div>
        <div className="sm:col-span-2"><Label htmlFor="iq-phone">Telefon / WhatsApp (optional, oder statt E-Mail)</Label><Input id="iq-phone" value={f.phone} onChange={set("phone")} autoComplete="tel" data-testid="inquiry-phone" /></div>
      </div>
      {type === "interior" && (
        <div className="grid sm:grid-cols-3 gap-4">
          <div><Label htmlFor="iq-b">Budget</Label><Input id="iq-b" value={f.budget} onChange={set("budget")} data-testid="inquiry-budget" /></div>
          <div><Label htmlFor="iq-l">Ort</Label><Input id="iq-l" value={f.location} onChange={set("location")} data-testid="inquiry-location" /></div>
          <div><Label htmlFor="iq-t">Zeitraum</Label><Input id="iq-t" value={f.timeframe} onChange={set("timeframe")} data-testid="inquiry-timeframe" /></div>
        </div>
      )}
      <div><Label htmlFor="iq-msg">Nachricht</Label><Textarea id="iq-msg" rows={4} value={f.message} onChange={set("message")} maxLength={5000} data-testid="inquiry-message" /></div>
      {photos && (
        <div>
          <Label htmlFor="iq-files">{type === "interior" ? "Raumfotos" : "Bilder"} (optional, max. 6 · JPG/PNG/WebP · je max. 8 MB · nur intern sichtbar)</Label>
          <Input id="iq-files" type="file" multiple accept="image/jpeg,image/png,image/webp" onChange={(e) => setFiles([...e.target.files].slice(0, 6))} data-testid="inquiry-photos" />
        </div>
      )}
      <label className="flex gap-3 items-start text-sm"><input type="checkbox" className="mt-1 h-5 w-5" checked={f.privacy} onChange={set("privacy")} data-testid="inquiry-privacy" />
        <span>Ich habe den <a href="/rechtliches/datenschutz" target="_blank" className="underline">Datenschutzhinweis</a> gelesen. Meine Angaben werden nur zur Bearbeitung dieser Anfrage verwendet (kein Newsletter).</span></label>
      {spam.widget}
      {err && <p className="text-sm text-[#c2181d] font-semibold" role="alert" data-testid="inquiry-error">{err}</p>}
      <button type="submit" disabled={busy || !spam.ready} className="btn-ink h-12 px-6 font-semibold w-full sm:w-auto disabled:opacity-60" data-testid="inquiry-submit">{busy ? "Wird gesendet …" : "Anfrage senden"}</button>
    </form>
  );
}

export function InquiryDialog({ open, onOpenChange, title, ...props }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto bg-[#FAF8F5]" data-testid="inquiry-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl font-black">{title}</DialogTitle><DialogDescription>Wir antworten persönlich. Pflichtfelder sind mit * markiert.</DialogDescription></DialogHeader>
        <InquiryForm {...props} />
      </DialogContent>
    </Dialog>
  );
}


